import { effectScope, onScopeDispose, readonly, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { defineStore } from 'pinia';
import { nanoid } from '@sa/utils';
import { sessionStg } from '@/utils/storage';
import { type SseClient, createSseConnection } from '@/utils/sse-client';
import { NotificationActionEnum, SetupStoreId, SseEventTypeEnum } from '@/enum';
import { useAuthStore } from '../auth';

/** Theme store */
export const useNotifyStore = defineStore(SetupStoreId.Notify, () => {
  const scope = effectScope();
  const authStore = useAuthStore();

  /** Theme settings */
  const unreadCount: Ref<number> = ref(0);
  const unreadAction: Ref<Record<NotificationActionEnum, number>> = ref({
    [NotificationActionEnum.NOTIFY]: 0,
    [NotificationActionEnum.TODO]: 0
  });
  const lastReadNotificationId: Ref<string | null> = ref(null);
  const sseClient: Ref<SseClient | null> = ref(null);

  async function connectNotifySse(): Promise<SseClient> {
    const clientId = sessionStg.get('clientId') ?? nanoid();
    sessionStg.set('clientId', clientId);
    const client = await createSseConnection(`/sse/connect?clientId=${clientId}`, { onMessage: dealMsg });
    return client;
  }
  function dealMsg(msg: string) {
    if (msg.startsWith('data:')) {
      // eslint-disable-next-line no-param-reassign
      msg = msg.substring(5);
    }
    if (msg.trim() === '') {
      return;
    }
    const { event, data } = JSON.parse(msg);
    switch (event) {
      case SseEventTypeEnum.CONNECT:
        console.log('connect', data);
        break;
      case SseEventTypeEnum.HEARTBEAT:
        console.log('heartbeat', data);
        break;
      case SseEventTypeEnum.NOTIFY_STATISTICS:
        // eslint-disable-next-line no-case-declarations
        const unreadNum = (data as Array<any>).reduce((prev, cur) => prev + cur.unreadNum, 0);
        data.forEach((item: any) => {
          unreadAction.value[item.action as NotificationActionEnum] = item.unreadNum;
        });
        unreadCount.value = unreadNum;
        break;
      // eslint-disable-next-line no-fallthrough
      case SseEventTypeEnum.NOTIFY_NEW:
        window.$notification?.info({
          title: data?.notification?.title,
          content: data?.notification?.content
        });
        break;
      case SseEventTypeEnum.NOTIFY_READ:
        lastReadNotificationId.value = data;
        break;
      default:
        console.log(event, data);
    }
  }

  // watch store
  scope.run(() => {
    watch(
      () => authStore.isLogin,
      async isLogin => {
        if (isLogin) {
          sseClient.value = await connectNotifySse();
        } else {
          sseClient.value?.close();
          sseClient.value = null;
        }
      },
      { immediate: true }
    );
  });

  /** On scope dispose */
  onScopeDispose(() => {
    scope.stop();
  });

  /** Update unread count */
  function readNotify(action: NotificationActionEnum) {
    unreadAction.value[action] -= 1;
    unreadCount.value -= 1;
  }

  return {
    unreadCount: readonly(unreadCount),
    unreadAction: readonly(unreadAction),
    lastReadNotificationId: readonly(lastReadNotificationId),
    readNotify
  };
});
